import type {
  ApprovalRecord,
  CardAction,
  CardActionResult,
  Claim,
  FactInfo,
  Fix,
  LenderOption,
  NoteSection,
  Pack,
  StaffRef,
} from './types';
import { toPricingResult } from './pricingWire';
import { scalar, str as readStr } from './wireValues';

// The crew's wire names (Plan 4a "Contract for Plan 4b"): the pack card body
// (snake_case, spec §8), the approval record and the card-action reply. As in
// wire.ts, malformed pieces are dropped rather than thrown on, so one odd field
// never hides the whole pack.

type Obj = Record<string, unknown>;

const obj = (v: unknown): Obj | null =>
  typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Obj) : null;
const str = (v: unknown, fallback = ''): string => readStr(v) ?? fallback;
const num = (v: unknown, fallback = 0): number =>
  typeof v === 'number' ? v : fallback;
const list = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const strings = (v: unknown): string[] =>
  list(v).filter((x): x is string => typeof x === 'string');
const present = <T>(v: T | null): v is T => v !== null;

export const BROKER_NOTE_SECTIONS = [
  'loan_purpose',
  'applicant_overview',
  'living_conditions',
  'employment_income',
  'commitments',
  'others',
  'mitigants',
] as const;

export const COMPLIANCE_NOTE_SECTIONS = [
  'loan_objective',
  'loan_requirement',
  'loan_circumstances',
  'loan_financial_awareness',
  'loan_prioritized',
  'lender_loan',
  'loan_structure',
  'goals_objectives',
] as const;

/** "loan_financial_awareness" -> "Loan financial awareness". */
export const sectionLabel = (key: string): string => {
  const words = key.split('_').join(' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const toClaim = (v: unknown): Claim | null => {
  const o = obj(v);
  return o && typeof o.text === 'string'
    ? { text: o.text, cites: strings(o.cites) }
    : null;
};
const claims = (v: unknown): Claim[] => list(v).map(toClaim).filter(present);

const toFix = (v: unknown): Fix | null => {
  const o = obj(v);
  return o
    ? {
        fieldPath: str(o.field_path),
        problem: str(o.problem),
        cites: strings(o.cites),
      }
    : null;
};
const fixes = (v: unknown): Fix[] => list(v).map(toFix).filter(present);

function toOption(v: unknown): LenderOption | null {
  const o = obj(v);
  if (!o) return null;
  return {
    rank: num(o.rank),
    lenderId: str(o.lender_id),
    lenderName: str(o.lender_name),
    productId: str(o.product_id),
    productName: str(o.product_name),
    reasons: claims(o.reasons),
    rate: toClaim(o.rate) ?? { text: '', cites: [] },
    risks: claims(o.risks),
  };
}

const sections = (v: unknown, keys: readonly string[]): NoteSection[] => {
  const o = obj(v) ?? {};
  return keys.map((key) => ({
    key,
    label: sectionLabel(key),
    claims: claims(o[key]),
  }));
};

function toFacts(v: unknown): Record<string, FactInfo> {
  const out: Record<string, FactInfo> = {};
  for (const [id, raw] of Object.entries(obj(v) ?? {})) {
    const f = obj(raw);
    if (!f) continue;
    out[id] = {
      label: str(f.label, id),
      value: scalar(f.value),
      source: str(f.source),
      tool: str(f.tool),
    };
  }
  return out;
}

/** The pack card's body (Plan 4a), or null when it isn't an object. */
export function toPack(body: unknown): Pack | null {
  const b = obj(body);
  if (!b) return null;
  const draft = obj(b.draft);
  const noFit: Record<string, unknown> = obj(draft?.no_fit) ?? {};
  const notes = obj(b.notes);
  const review = obj(b.review);
  return {
    assessmentId: str(b.assessment_id),
    draft: draft
      ? {
          shortlist: list(draft.shortlist)
            .map(toOption)
            .filter(present)
            .sort((x, y) => x.rank - y.rank),
          noFit: { gaps: strings(noFit.gaps), reasons: claims(noFit.reasons) },
          structure: claims(draft.structure),
          assumptions: strings(draft.assumptions),
        }
      : null,
    notes: notes
      ? {
          brokerNote: sections(notes.broker_note, BROKER_NOTE_SECTIONS),
          complianceNote: sections(
            notes.compliance_note,
            COMPLIANCE_NOTE_SECTIONS,
          ),
        }
      : null,
    review: review
      ? {
          verdict: review.verdict === 'pass' ? 'pass' : 'revise',
          fixes: fixes(review.fixes),
        }
      : null,
    rounds: num(b.rounds),
    unreviewed: b.unreviewed === true,
    trial: b.trial === true,
    incomplete: b.incomplete === true,
    noEligibleLender: b.no_eligible_lender === true,
    // Warn only when the server says so explicitly.
    independentReview: b.independent_review !== false,
    gaps: strings(b.gaps),
    checkFixes: fixes(b.check_fixes),
    facts: toFacts(b.facts),
  };
}

const toStaff = (v: unknown): StaffRef => {
  const o = obj(v) ?? {};
  return {
    id: str(o.id),
    email: str(o.email),
    name: typeof o.name === 'string' ? o.name : null,
  };
};

/** The approvalRecord in an approve/reject reply (snake_case, spec §8). */
export function toApprovalRecord(v: unknown): ApprovalRecord | null {
  const o = obj(v);
  if (!o || typeof o.run_id !== 'string') return null;
  const decision =
    o.decision === 'approve' || o.decision === 'reject' ? o.decision : null;
  if (!decision) return null;
  const hash = Object.entries(obj(o.instructions_hash) ?? {}).filter(
    (entry): entry is [string, string] => typeof entry[1] === 'string',
  );
  return {
    runId: o.run_id,
    assessmentId: str(o.assessment_id),
    requester: toStaff(o.requester),
    approver: toStaff(o.approver),
    approverRole: str(o.approver_role),
    decision,
    reason: typeof o.reason === 'string' ? o.reason : null,
    at: str(o.at),
    gitSha: str(o.git_sha),
    instructionsHash: Object.fromEntries(hash),
    unreviewed: o.unreviewed === true,
    demoOnly: o.demo_only !== false,
  };
}

const CARD_ACTIONS: readonly CardAction[] = [
  'start',
  'cancel',
  'approve',
  'reject',
  'confirm',
  'discard',
];

/** The reply to POST /runs/{runId}/cards/{cardId} (camelCase at the top level). */
export function toCardActionResult(body: unknown): CardActionResult | null {
  const o = obj(body);
  const action = CARD_ACTIONS.find((a) => a === o?.action);
  if (!o || !action) return null;
  const pricing = toPricingResult(o.pricing);
  return {
    action,
    runId: typeof o.runId === 'string' && o.runId !== '' ? o.runId : null,
    approvalRecord: toApprovalRecord(o.approvalRecord),
    ...(pricing ? { pricing } : {}),
  };
}

/** POST /runs/{runId}/cards/{cardId} body; the server rejects unknown fields. */
export const toCardActionBody = (
  action: CardAction,
  reason?: string,
  selected?: string[],
) => ({
  action,
  ...(reason ? { reason } : {}),
  ...(selected ? { selected } : {}),
});

/** The `detail` code of an error reply ("card_closed", …). Used to choose a message, never shown. */
export const toErrorCode = (body: unknown): string => str(obj(body)?.detail);
